/**
 * Reports Configuration
 * 
 * This file defines all available reports in the system.
 * When adding a new report, simply add it to the AVAILABLE_REPORTS array.
 * The system will automatically:
 * - Include it in permission configuration
 * - Show it in the reports menu (if user has permission)
 * - Allow role-based access control
 */

export interface ReportConfig {
  id: string;
  name: string;
}

/**
 * List of all available reports in the system
 * 
 * To add a new report:
 * 1. Add a new entry to this array with a unique id and display name
 * 2. The report will automatically appear in:
 *    - Role permission configuration (Edit Role modal)
 *    - Reports menu (if user has permission)
 *    - Permission checking system
 */
export const AVAILABLE_REPORTS: ReportConfig[] = [
  { id: 'work-order-by-item', name: 'Work Order by Items' },
  { id: 'order-status', name: 'Order Status Report' },
  { id: 'employee-staff-work-summary', name: 'Employee Staff Work Summary' },
  { id: 'work-order-frames', name: 'Work Order Only Frames' },
  { id: 'employee-monthly', name: 'Employee Monthly Report' },
  { id: 'employee-yearly', name: 'Employee Yearly Report' },
  // Add new reports here:
  // { id: 'new-report-id', name: 'New Report Name' },
];

/**
 * Get all report IDs as an array
 */
export const getReportIds = (): string[] => {
  return AVAILABLE_REPORTS.map(report => report.id);
};

/**
 * Get report name by ID
 */
export const getReportName = (reportId: string): string | undefined => {
  const report = AVAILABLE_REPORTS.find(r => r.id === reportId);
  return report?.name;
};

/**
 * Check if a report ID exists
 */
export const isValidReportId = (reportId: string): boolean => {
  return AVAILABLE_REPORTS.some(r => r.id === reportId);
};

